import { useMyTeam } from "@coworker/app/src/core/hooks/useStoreTeams";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateStringType } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { AssignTo } from "@coworker/apprestructured/src/task-management/components/AssignTo/AssignTo";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { OnConfirmClickResult } from "../../../AssignTo/AssignToModal";
import { useAssignableTeamsAndUsers } from "./useAssignableTeamsAndUsers";
import { useMostRecentAssignedTask } from "@coworker/apprestructured/src/task-management/services/useMostRecentAssignedTask";

export type AssignToCreatorFragmentConfigProps = EmptyObject;

type AssignToCreatorFragment = {
  configProps: AssignToCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AssignToCreatorFragment({
  commonProps,
  ref,
}: AssignToCreatorFragment) {
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;
  const assignedUserId =
    validateStringType(getByPath(task, "assigned_user_id")) ?? "";
  const assignedTeamId =
    validateStringType(getByPath(task, "assigned_team_id")) ?? "";

  const [searchQuery, setSearchQuery] = useState("");

  const {
    assignableTeams,
    activeUsers,
    isActiveUsersLoading,
    isTeamsLoading,
    teams,
  } = useAssignableTeamsAndUsers();

  const myTeam = useMyTeam();
  const { data: mostRecentAssignedTask } = useMostRecentAssignedTask(
    isLoading(taskType) ? null : taskType.id,
    isLoading(task) ? undefined : task["details"]
  );
  const mostRecentAssignedUser = activeUsers.find(
    (user) =>
      user.uid === mostRecentAssignedTask?.assigned_user_id &&
      assignableTeams.some((team) => team.id === user.teamId)
  );
  const mostRecentAssignedTeam = mostRecentAssignedTask?.assigned_user_id
    ? undefined
    : assignableTeams.find(
        (team) => team.id === mostRecentAssignedTask?.assigned_team_id
      );

  const description = useMemo(() => {
    if (isLoading(assignedUserId) || isLoading(assignedTeamId)) {
      return "";
    }

    return assignedUserId
      ? (activeUsers || []).find((user) => user.uid === assignedUserId)
          ?.fullName || ""
      : assignedTeamId
        ? teams?.find((team) => team.id === assignedTeamId)?.name || ""
        : "";
  }, [activeUsers, assignedTeamId, assignedUserId, teams]);

  const handleConfirmClick = useCallback(
    ({ teamId, userId }: OnConfirmClickResult) => {
      onTaskChange({
        assigned_user_id: userId,
        assigned_team_id: teamId,
      });
    },
    [onTaskChange]
  );

  const productQualityTeamId = useProductQualityTeamId();
  const isProductQuality =
    !isLoading(taskType) && taskType?.name === TaskTypeNames.PRODUCT_QUALITY;

  useEffect(() => {
    if (
      isProductQuality &&
      productQualityTeamId &&
      !isLoading(assignedTeamId) &&
      !assignedTeamId
    ) {
      onTaskChange(
        { assigned_team_id: productQualityTeamId },
        { silent: true }
      );
    }
  }, [isProductQuality, productQualityTeamId, assignedTeamId, onTaskChange]);

  const hasAssignableTeams = assignableTeams.length > 0;

  // Valid only if assigned_team_id is found in teams list
  const isTeamFound =
    !isTeamsLoading &&
    hasAssignableTeams &&
    !!assignableTeams?.some((team) => team.id === assignedTeamId);

  const isUserIdFound =
    !isActiveUsersLoading &&
    (activeUsers || []).some((user) => user.uid === assignedUserId);

  const isValidAssignedTeamId =
    !isLoading(assignedTeamId) && !!assignedTeamId && isTeamFound;
  const isValidAssignedUserId =
    !isLoading(assignedUserId) && !!assignedUserId && isUserIdFound;

  const valid = assignedUserId
    ? isValidAssignedTeamId && isValidAssignedUserId
    : isValidAssignedTeamId;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  const isDisabled =
    blockInput ||
    isLoading(assignedTeamId) ||
    isLoading(assignedUserId) ||
    isLoading(taskType) ||
    isProductQuality;

  return (
    <AssignTo
      assignedTeamId={isLoading(assignedTeamId) ? null : assignedTeamId}
      assignedUserId={isLoading(assignedUserId) ? null : assignedUserId}
      disabled={isDisabled}
      description={description}
      searchQuery={searchQuery}
      isTeamsLoading={isTeamsLoading}
      myTeamId={myTeam?.id || ""}
      isUsersLoading={isActiveUsersLoading}
      teams={assignableTeams}
      users={activeUsers}
      mostRecentAssignedUser={mostRecentAssignedUser}
      mostRecentAssignedTeam={mostRecentAssignedTeam}
      isError={!valid && showValidation}
      onConfirmClick={handleConfirmClick}
      onSearchQueryChange={setSearchQuery}
    />
  );
}

export default AssignToCreatorFragment;
